<template>
  <head>
    <meta charset="utf-8" />
    <title>CBHI | Member Profile</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="Individual member health profile" name="description" />
    <meta content="CBHI System" name="author" />
    <!-- App favicon -->
    <link rel="shortcut icon" href="assets/images/favicon.svg">

    <!-- Bootstrap Css -->
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <!-- Icons Css -->
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <!-- App Css-->
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <!-- custom Css-->
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- ========== Left Sidebar Start ========== -->
    <NavigationBar/>
    <!-- Left Sidebar End -->

    <!-- ============================================================== -->
    <!-- Start right Content here -->
    <!-- ============================================================== -->
    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid">
          <!-- start page title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Member Profile</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">HEW</a></li>
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Members</a></li>
                    <li class="breadcrumb-item active">Profile</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Alerts -->
          <div v-if="alert.success" class="alert alert-success" role="alert">
            {{message}}
          </div>
          <div v-if="alert.info" class="alert alert-primary" role="alert">
            {{message}}
          </div>
          <div v-if="alert.failed" class="alert alert-danger" role="alert">
            {{message}}
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading member profile...</p>
          </div>

          <!-- Member Profile Content -->
          <div v-else-if="member">
            <!-- Member Header Card -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-8">
                        <div class="d-flex align-items-center mb-3">
                          <div class="avatar-lg me-3">
                            <div class="avatar-title bg-primary text-white rounded-circle font-size-24">
                              {{ getInitials(member.first_name, member.surname) }}
                            </div>
                          </div>
                          <div>
                            <h4 class="mb-1">{{ member.first_name }} {{ member.middle_name }} {{ member.surname }}</h4>
                            <p class="text-muted mb-0">
                              <i class="bx bx-id-card me-1"></i> {{ member.cbhi_number || 'No CBHI Number' }}
                              <span v-if="member.enrollment_type === 'cbhi_member'" class="badge bg-success ms-2">
                                CBHI Active
                              </span>
                              <span v-else class="badge bg-secondary ms-2">
                                Non-CBHI
                              </span>
                            </p>
                          </div>
                        </div>

                        <div class="row text-center text-md-start">
                          <div class="col-md-3 col-6">
                            <h6 class="text-muted mb-1">Age</h6>
                            <p class="mb-0 fw-bold">{{ calculateAge(member.date_of_birth) }}</p>
                          </div>
                          <div class="col-md-3 col-6">
                            <h6 class="text-muted mb-1">Gender</h6>
                            <p class="mb-0 fw-bold">{{ member.gender === 'M' ? 'Male' : 'Female' }}</p>
                          </div>
                          <div class="col-md-3 col-6">
                            <h6 class="text-muted mb-1">Household</h6>
                            <p class="mb-0 fw-bold">
                              <a href="#" @click="goToHousehold">{{ member.household_number }}</a>
                            </p>
                          </div>
                          <div class="col-md-3 col-6">
                            <h6 class="text-muted mb-1">Relationship</h6>
                            <p class="mb-0 fw-bold">{{ formatRelationship(member.relationship_to_head) }}</p>
                          </div>
                        </div>
                      </div>

                      <div class="col-md-4 text-md-end mt-3 mt-md-0">
                        <div class="btn-group" role="group">
                          <button @click="showImmunizationModal = true" class="btn btn-success">
                            <i class="bx bx-injection"></i> Immunize
                          </button>
                          <button @click="showReferralModal = true" class="btn btn-warning">
                            <i class="bx bx-transfer"></i> Refer
                          </button>
                          <button @click="editMember" class="btn btn-primary">
                            <i class="bx bx-edit"></i> Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Health Status Cards -->
            <div class="row">
              <!-- Vaccination Status -->
              <div class="col-lg-3 col-md-6">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-2">Vaccination Status</p>
                        <h5 class="mb-0">
                          <span v-if="healthProfile.vaccination_status === 'up_to_date'" class="text-success">
                            Up to Date
                          </span>
                          <span v-else-if="healthProfile.vaccination_status === 'partial'" class="text-warning">
                            Partial
                          </span>
                          <span v-else class="text-danger">
                            Not Started
                          </span>
                        </h5>
                      </div>
                      <div class="avatar-sm ms-auto">
                        <div :class="'avatar-title rounded-circle text-white font-size-20 ' + getVaccinationStatusColor()">
                          <i class="bx bx-injection"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Nutritional Status -->
              <div class="col-lg-3 col-md-6">
                <div class="card blog-stats-wid">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-2">Nutritional Status</p>
                        <h5 class="mb-0">
                          <span v-if="healthProfile.nutritional_status === 'normal'" class="text-success">
                            Normal
                          </span>
                          <span v-else-if="healthProfile.nutritional_status === 'MAM'" class="text-warning">
                            MAM
                          </span>
                          <span v-else-if="healthProfile.nutritional_status === 'SAM'" class="text-danger">
                            SAM
                          </span>
                          <span v-else class="text-muted">
                            Not Assessed
                          </span>
                        </h5>
                      </div>
                      <div class="avatar-sm ms-auto">
                        <div :class="'avatar-title rounded-circle text-white font-size-20 ' + getNutritionalStatusColor()">
                          <i class="bx bx-heart"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Last Health Check -->
              <div class="col-lg-3 col-md-6">
                <div class="card blog-stats-wid">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-2">Last Health Check</p>
                        <h5 class="mb-0">
                          {{ healthProfile.last_health_check_date ? formatDate(healthProfile.last_health_check_date) : 'Never' }}
                        </h5>
                      </div>
                      <div class="avatar-sm ms-auto">
                        <div class="avatar-title bg-info rounded-circle text-white font-size-20">
                          <i class="bx bx-calendar-check"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Special Status -->
              <div class="col-lg-3 col-md-6">
                <div class="card blog-stats-wid">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-2">Special Status</p>
                        <div>
                          <span v-if="activePregnancy" class="badge bg-danger">Pregnant</span>
                          <span v-if="isUnderFive" class="badge bg-warning">Under 5</span>
                          <span v-if="healthProfile.chronic_conditions" class="badge bg-info">Chronic</span>
                          <span v-if="!activePregnancy && !isUnderFive && !healthProfile.chronic_conditions"
                                class="text-muted">None</span>
                        </div>
                      </div>
                      <div class="avatar-sm ms-auto">
                        <div class="avatar-title bg-primary rounded-circle text-white font-size-20">
                          <i class="bx bx-user-check"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tab Navigation -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <ul class="nav nav-tabs nav-tabs-custom nav-justified" role="tablist">
                      <li class="nav-item">
                        <a class="nav-link active" data-bs-toggle="tab" href="#health-info" role="tab">
                          <span class="d-block d-sm-none"><i class="bx bx-health"></i></span>
                          <span class="d-none d-sm-block">Health Information</span>
                        </a>
                      </li>
                      <li class="nav-item">
                        <a class="nav-link" data-bs-toggle="tab" href="#immunizations" role="tab">
                          <span class="d-block d-sm-none"><i class="bx bx-injection"></i></span>
                          <span class="d-none d-sm-block">Immunizations</span>
                        </a>
                      </li>
                      <li class="nav-item" v-if="member.gender === 'F' && isReproductiveAge">
                        <a class="nav-link" data-bs-toggle="tab" href="#maternal-health" role="tab">
                          <span class="d-block d-sm-none"><i class="bx bx-female"></i></span>
                          <span class="d-none d-sm-block">Maternal Health</span>
                        </a>
                      </li>
                      <li class="nav-item" v-if="isUnderFive">
                        <a class="nav-link" data-bs-toggle="tab" href="#growth-monitoring" role="tab">
                          <span class="d-block d-sm-none"><i class="bx bx-trending-up"></i></span>
                          <span class="d-none d-sm-block">Growth Monitoring</span>
                        </a>
                      </li>
                      <li class="nav-item">
                        <a class="nav-link" data-bs-toggle="tab" href="#visit-history" role="tab">
                          <span class="d-block d-sm-none"><i class="bx bx-history"></i></span>
                          <span class="d-none d-sm-block">Visit History</span>
                        </a>
                      </li>
                    </ul>

                    <!-- Tab Content -->
                    <div class="tab-content p-3">
                      <!-- Health Information Tab -->
                      <div class="tab-pane active" id="health-info" role="tabpanel">
                        <div class="row">
                          <div class="col-md-6">
                            <h5 class="mb-3">Basic Health Information</h5>
                            <table class="table table-bordered">
                              <tbody>
                              <tr>
                                <th width="40%">Blood Group</th>
                                <td>{{ healthProfile.blood_group || 'Unknown' }}</td>
                              </tr>
                              <tr>
                                <th>Chronic Conditions</th>
                                <td>{{ healthProfile.chronic_conditions || 'None' }}</td>
                              </tr>
                              <tr>
                                <th>Disabilities</th>
                                <td>{{ healthProfile.disabilities || 'None' }}</td>
                              </tr>
                              <tr>
                                <th>Allergies</th>
                                <td>{{ healthProfile.allergies || 'None' }}</td>
                              </tr>
                              <tr>
                                <th>Current Medications</th>
                                <td>{{ healthProfile.current_medications || 'None' }}</td>
                              </tr>
                              </tbody>
                            </table>
                          </div>

                          <div class="col-md-6">
                            <h5 class="mb-3">Contact Information</h5>
                            <table class="table table-bordered">
                              <tbody>
                              <tr>
                                <th width="40%">Phone Number</th>
                                <td>{{ member.phone_number || 'Not provided' }}</td>
                              </tr>
                              <tr>
                                <th>Emergency Contact</th>
                                <td>{{ member.emergency_contact_name || 'Not provided' }}</td>
                              </tr>
                              <tr>
                                <th>Emergency Phone</th>
                                <td>{{ member.emergency_contact_phone || 'Not provided' }}</td>
                              </tr>
                              <tr>
                                <th>Marital Status</th>
                                <td>{{ formatMaritalStatus(member.marital_status) }}</td>
                              </tr>
                              <tr>
                                <th>Education Level</th>
                                <td>{{ formatEducationLevel(member.education_level) }}</td>
                              </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div class="mt-3 text-end">
                          <button @click="updateHealthProfile" class="btn btn-primary">
                            <i class="bx bx-edit"></i> Update Health Information
                          </button>
                        </div>
                      </div>

                      <!-- Immunizations Tab -->
                      <div class="tab-pane" id="immunizations" role="tabpanel">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                          <h5 class="mb-0">Immunization History</h5>
                          <button @click="showImmunizationModal = true" class="btn btn-sm btn-success">
                            <i class="bx bx-plus"></i> Add Immunization
                          </button>
                        </div>

                        <div v-if="immunizations.length === 0" class="text-center py-4">
                          <i class="bx bx-injection text-muted" style="font-size: 48px;"></i>
                          <p class="mt-3 text-muted">No immunization records found</p>
                        </div>

                        <div v-else class="table-responsive">
                          <table class="table table-hover">
                            <thead>
                            <tr>
                              <th>Vaccine</th>
                              <th>Dose</th>
                              <th>Date Given</th>
                              <th>Age</th>
                              <th>Given By</th>
                              <th>Next Dose</th>
                              <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="vaccine in immunizations" :key="vaccine.immunization_id">
                              <td>{{ vaccine.vaccine_name }}</td>
                              <td>Dose {{ vaccine.dose_number }}</td>
                              <td>{{ formatDate(vaccine.date_given) }}</td>
                              <td>{{ formatAgeAtImmunization(vaccine.age_at_immunization) }}</td>
                              <td>{{ vaccine.given_by_name }}</td>
                              <td>
                                  <span v-if="vaccine.next_dose_date"
                                        :class="{'text-danger': isPastDue(vaccine.next_dose_date)}">
                                    {{ formatDate(vaccine.next_dose_date) }}
                                  </span>
                                <span v-else class="text-muted">Complete</span>
                              </td>
                              <td>
                                <button @click="viewImmunizationDetails(vaccine)"
                                        class="btn btn-sm btn-primary">
                                  <i class="bx bx-show"></i>
                                </button>
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>

                        <!-- Due Vaccines Alert -->
                        <div v-if="dueVaccines.length > 0" class="alert alert-warning mt-3">
                          <h6 class="alert-heading">
                            <i class="bx bx-error-circle"></i> Due Vaccines
                          </h6>
                          <ul class="mb-0">
                            <li v-for="due in dueVaccines" :key="due.vaccine_name">
                              {{ due.vaccine_name }} - Dose {{ due.dose_number }}
                              <span v-if="due.is_overdue" class="text-danger">
                                ({{ due.days_overdue }} days overdue)
                              </span>
                            </li>
                          </ul>
                        </div>
                      </div>

                      <!-- Maternal Health Tab -->
                      <div v-if="member.gender === 'F' && isReproductiveAge" class="tab-pane" id="maternal-health" role="tabpanel">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                          <h5 class="mb-0">Pregnancy & ANC History</h5>
                          <button v-if="!activePregnancy" @click="showPregnancyModal = true"
                                  class="btn btn-sm btn-warning">
                            <i class="bx bx-plus"></i> Register Pregnancy
                          </button>
                        </div>

                        <!-- Active Pregnancy -->
                        <div v-if="activePregnancy" class="card border-danger mb-3">
                          <div class="card-body">
                            <h6 class="card-title text-danger">Current Pregnancy</h6>
                            <div class="row">
                              <div class="col-md-3">
                                <p class="text-muted mb-1">LMP Date</p>
                                <p class="fw-bold">{{ formatDate(activePregnancy.lmp_date) }}</p>
                              </div>
                              <div class="col-md-3">
                                <p class="text-muted mb-1">EDD</p>
                                <p class="fw-bold">{{ formatDate(activePregnancy.edd_date) }}</p>
                              </div>
                              <div class="col-md-3">
                                <p class="text-muted mb-1">Gestational Age</p>
                                <p class="fw-bold">{{ calculateGestationalAge(activePregnancy.lmp_date) }} weeks</p>
                              </div>
                              <div class="col-md-3">
                                <p class="text-muted mb-1">ANC Visits</p>
                                <p class="fw-bold">{{ activePregnancy.anc_visits || 0 }}</p>
                              </div>
                            </div>
                            <div class="mt-3">
                              <button @click="showANCModal = true" class="btn btn-primary">
                                <i class="bx bx-plus-medical"></i> Record ANC Visit
                              </button>
                            </div>
                          </div>
                        </div>

                        <!-- Pregnancy History -->
                        <h6 class="mb-3">Pregnancy History</h6>
                        <div v-if="pregnancyHistory.length === 0" class="text-center py-4">
                          <p class="text-muted">No pregnancy history</p>
                        </div>
                        <div v-else class="table-responsive">
                          <table class="table table-sm">
                            <thead>
                            <tr>
                              <th>#</th>
                              <th>Year</th>
                              <th>Outcome</th>
                              <th>ANC Visits</th>
                              <th>Delivery Place</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="preg in pregnancyHistory" :key="preg.pregnancy_id">
                              <td>{{ preg.pregnancy_number }}</td>
                              <td>{{ new Date(preg.edd_date).getFullYear() }}</td>
                              <td>{{ preg.outcome || 'Ongoing' }}</td>
                              <td>{{ preg.anc_visits }}</td>
                              <td>{{ preg.delivery_place || '-' }}</td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <!-- Growth Monitoring Tab -->
                      <div v-if="isUnderFive" class="tab-pane" id="growth-monitoring" role="tabpanel">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                          <h5 class="mb-0">Growth Monitoring</h5>
                          <button @click="showGrowthModal = true" class="btn btn-sm btn-success">
                            <i class="bx bx-plus"></i> Add Measurement
                          </button>
                        </div>

                        <!-- Growth Chart would go here -->
                        <div class="card mb-3">
                          <div class="card-body">
                            <h6>Latest Measurements</h6>
                            <div v-if="latestGrowth" class="row">
                              <div class="col-md-3">
                                <p class="text-muted mb-1">Weight</p>
                                <p class="fw-bold">{{ latestGrowth.weight }} kg</p>
                              </div>
                              <div class="col-md-3">
                                <p class="text-muted mb-1">Height</p>
                                <p class="fw-bold">{{ latestGrowth.height }} cm</p>
                              </div>
                              <div class="col-md-3">
                                <p class="text-muted mb-1">MUAC</p>
                                <p class="fw-bold">{{ latestGrowth.muac }} cm</p>
                              </div>
                              <div class="col-md-3">
                                <p class="text-muted mb-1">Status</p>
                                <p class="fw-bold">
                                  <span :class="getNutritionalStatusBadge(latestGrowth.nutritional_status)">
                                    {{ latestGrowth.nutritional_status }}
                                  </span>
                                </p>
                              </div>
                            </div>
                            <p v-else class="text-muted">No growth measurements recorded</p>
                          </div>
                        </div>

                        <!-- Growth History -->
                        <div v-if="growthHistory.length > 0" class="table-responsive">
                          <table class="table table-sm">
                            <thead>
                            <tr>
                              <th>Date</th>
                              <th>Age</th>
                              <th>Weight</th>
                              <th>Height</th>
                              <th>MUAC</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="growth in growthHistory" :key="growth.growth_id">
                              <td>{{ formatDate(growth.measurement_date) }}</td>
                              <td>{{ growth.age_months }} months</td>
                              <td>{{ growth.weight }} kg</td>
                              <td>{{ growth.height }} cm</td>
                              <td>{{ growth.muac }} cm</td>
                              <td>
                                  <span :class="getNutritionalStatusBadge(growth.nutritional_status)">
                                    {{ growth.nutritional_status }}
                                  </span>
                              </td>
                              <td>
                                <button @click="viewGrowthDetails(growth)"
                                        class="btn btn-sm btn-primary">
                                  <i class="bx bx-show"></i>
                                </button>
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <!-- Visit History Tab -->
                      <div class="tab-pane" id="visit-history" role="tabpanel">
                        <h5 class="mb-3">Visit History</h5>

                        <div v-if="visitHistory.length === 0" class="text-center py-4">
                          <i class="bx bx-history text-muted" style="font-size: 48px;"></i>
                          <p class="mt-3 text-muted">No visit history found</p>
                        </div>

                        <div v-else>
                          <div v-for="visit in visitHistory" :key="visit.visit_id"
                               class="card mb-3">
                            <div class="card-body">
                              <div class="d-flex justify-content-between align-items-start">
                                <div>
                                  <h6 class="mb-1">{{ formatDate(visit.visit_date) }}</h6>
                                  <p class="text-muted mb-2">
                                    <i class="bx bx-user"></i> {{ visit.hew_name }} •
                                    <i class="bx bx-time-five"></i> {{ visit.visit_duration }} minutes
                                  </p>
                                  <p class="mb-2">
                                    <strong>Type:</strong> {{ formatVisitType(visit.visit_type) }}
                                  </p>
                                  <div v-if="visit.services && visit.services.length > 0">
                                    <strong>Services Provided:</strong>
                                    <ul class="mb-0">
                                      <li v-for="service in visit.services" :key="service.service_id">
                                        {{ service.service_type }} - {{ service.service_details }}
                                      </li>
                                    </ul>
                                  </div>
                                  <div v-if="visit.notes" class="mt-2">
                                    <strong>Notes:</strong> {{ visit.notes }}
                                  </div>
                                </div>
                                <button @click="viewVisitDetails(visit)" class="btn btn-sm btn-primary">
                                  <i class="bx bx-show"></i> Details
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No Member Found -->
          <div v-else-if="!loading" class="text-center py-5">
            <i class="bx bx-user-x text-muted" style="font-size: 48px;"></i>
            <p class="mt-3 text-muted">Member not found</p>
            <button @click="$router.go(-1)" class="btn btn-primary">
              <i class="bx bx-arrow-back"></i> Go Back
            </button>
          </div>
        </div>
        <!-- container-fluid -->
      </div>
      <!-- End Page-content -->

      <!-- Import Modal Components -->
      <ImmunizationModal
          v-if="showImmunizationModal"
          :preSelectedMember="member"
          @close="showImmunizationModal = false"
          @recorded="onImmunizationRecorded"
      />

      <PregnancyRegistrationModal
          v-if="showPregnancyModal"
          :member="member"
          @close="showPregnancyModal = false"
          @registered="onPregnancyRegistered"
      />

      <ReferralModal
          v-if="showReferralModal"
          :member="member"
          @close="showReferralModal = false"
          @created="onReferralCreated"
      />

      <ANCVisitModal
          v-if="showANCModal"
          :pregnancy="activePregnancy"
          @close="showANCModal = false"
          @recorded="onANCRecorded"
      />

      <GrowthMonitoringModal
          v-if="showGrowthModal"
          :member="member"
          @close="showGrowthModal = false"
          @recorded="onGrowthRecorded"
      />

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">© CBHI</div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">
                Ministry of Health - Ethiopia
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    <!-- end main content-->
  </div>
  <!-- END layout-wrapper -->

  <!-- Right bar overlay-->
  <div class="rightbar-overlay"></div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ImmunizationModal from "@/components/hew/modals/ImmunizationModal.vue";
import PregnancyRegistrationModal from "@/components/hew/modals/PregnancyRegistrationModal.vue";
import ReferralModal from "@/components/hew/modals/ReferralModal.vue";
import ANCVisitModal from "@/components/hew/modals/ANCVisitModal.vue";
import GrowthMonitoringModal from "@/components/hew/modals/GrowthMonitoringModal.vue";
import { authUrl, executeJson } from "@/api";
import router from "@/router";

export default {
  name: "MemberProfile",
  components: {
    NavigationBar,
    ImmunizationModal,
    PregnancyRegistrationModal,
    ReferralModal,
    ANCVisitModal,
    GrowthMonitoringModal
  },
  data() {
    return {
      // Loading states
      loading: false,

      // Member data
      member: null,
      healthProfile: {},
      immunizations: [],
      dueVaccines: [],
      pregnancyHistory: [],
      activePregnancy: null,
      growthHistory: [],
      latestGrowth: null,
      visitHistory: [],

      // UI States
      showImmunizationModal: false,
      showPregnancyModal: false,
      showReferralModal: false,
      showANCModal: false,
      showGrowthModal: false,

      // Alert states
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: ''
    };
  },

  computed: {
    memberId() {
      return sessionStorage.getItem("memberId")
    },

    isUnderFive() {
      if (!this.member?.date_of_birth) return false;
      const age = this.calculateAgeInMonths(this.member.date_of_birth);
      return age < 60;
    },

    isReproductiveAge() {
      if (!this.member?.date_of_birth) return false;
      const age = this.calculateAgeInYears(this.member.date_of_birth);
      return age >= 15 && age <= 49;
    }
  },

  mounted() {

    this.loadMemberProfile();
  },

  methods: {
    checkAuth() {
      const token = localStorage.getItem("authToken");
      const userRole = sessionStorage.getItem("userRoleId");

      if (!token) {
        router.push('/login');
        return;
      }

      // Check if user is HEW (role_id = 38)
      if (userRole !== '38') {
        this.showAlert('failed', 'Access denied. HEW role required.');
        router.push('/dashboard');
      }
    },

    async loadMemberProfile() {
      this.loading = true;

      const data = {
        "function": "getMemberProfile",
        "memberId": this.memberId
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.member = res.data.data.member;
          this.healthProfile = res.data.data.healthProfile || {};
          this.immunizations = res.data.data.immunizations || [];
          this.dueVaccines = res.data.data.dueVaccines || [];
          this.pregnancyHistory = res.data.data.pregnancyHistory || [];
          this.activePregnancy = res.data.data.activePregnancy;
          this.growthHistory = res.data.data.growthHistory || [];
          this.latestGrowth = res.data.data.latestGrowth;
          this.visitHistory = res.data.data.visitHistory || [];
        } else {
          this.showAlert('failed', res.data.message || 'Failed to load member profile');
        }
      } catch (e) {
        console.error("Failed to load member profile:", e);
        this.showAlert('failed', 'Failed to load member profile');
      } finally {
        this.loading = false;
      }
    },

    goToHousehold() {
      router.push(`/hew/households/${this.member.household_id}`);
    },

    editMember() {
      // Navigate to edit member page or open modal
      router.push(`/hew/members/${this.memberId}/edit`);
    },

    updateHealthProfile() {
      // Navigate to update health profile page or open modal
      router.push(`/hew/members/${this.memberId}/health-profile`);
    },

    viewImmunizationDetails(vaccine) {
      // Show immunization details modal
      console.log('View immunization:', vaccine);
    },

    viewGrowthDetails(growth) {
      // Show growth details modal
      console.log('View growth:', growth);
    },

    viewVisitDetails(visit) {
      // Navigate to visit details page
      router.push(`/hew/visits/${visit.visit_id}`);
    },

    // Modal callbacks
    onImmunizationRecorded() {
      this.showImmunizationModal = false;
      this.showAlert('success', 'Immunization recorded successfully');
      this.loadMemberProfile();
    },

    onPregnancyRegistered() {
      this.showPregnancyModal = false;
      this.showAlert('success', 'Pregnancy registered successfully');
      this.loadMemberProfile();
    },

    onReferralCreated() {
      this.showReferralModal = false;
      this.showAlert('success', 'Referral created successfully');
    },

    onANCRecorded() {
      this.showANCModal = false;
      this.showAlert('success', 'ANC visit recorded successfully');
      this.loadMemberProfile();
    },

    onGrowthRecorded() {
      this.showGrowthModal = false;
      this.showAlert('success', 'Growth monitoring recorded successfully');
      this.loadMemberProfile();
    },

    // Utility methods
    getInitials(firstName, lastName) {
      return ((firstName || '')[0] || '') + ((lastName || '')[0] || '');
    },

    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return 'Unknown';

      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      const years = today.getFullYear() - birthDate.getFullYear();
      const months = today.getMonth() - birthDate.getMonth();

      if (years === 0) {
        return `${months} months`;
      } else if (years < 5) {
        const totalMonths = years * 12 + months;
        return `${years} years ${months} months`;
      } else {
        return `${years} years`;
      }
    },

    calculateAgeInMonths(dateOfBirth) {
      if (!dateOfBirth) return 0;

      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      return (today.getFullYear() - birthDate.getFullYear()) * 12 +
          (today.getMonth() - birthDate.getMonth());
    },

    calculateAgeInYears(dateOfBirth) {
      if (!dateOfBirth) return 0;

      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      return today.getFullYear() - birthDate.getFullYear();
    },

    calculateGestationalAge(lmpDate) {
      if (!lmpDate) return 0;

      const today = new Date();
      const lmp = new Date(lmpDate);
      const diffTime = Math.abs(today - lmp);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.floor(diffDays / 7);
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatRelationship(relationship) {
      const relationships = {
        'head': 'Head of Household',
        'spouse': 'Spouse',
        'child': 'Child',
        'parent': 'Parent',
        'sibling': 'Sibling',
        'grandchild': 'Grandchild',
        'other': 'Other'
      };
      return relationships[relationship] || relationship;
    },

    formatMaritalStatus(status) {
      const statuses = {
        'single': 'Single',
        'married': 'Married',
        'divorced': 'Divorced',
        'widowed': 'Widowed',
        'separated': 'Separated'
      };
      return statuses[status] || status;
    },

    formatEducationLevel(level) {
      const levels = {
        'none': 'No Education',
        'primary': 'Primary',
        'secondary': 'Secondary',
        'tertiary': 'Tertiary',
        'vocational': 'Vocational'
      };
      return levels[level] || level;
    },

    formatVisitType(type) {
      const types = {
        'routine': 'Routine Health Promotion',
        'follow_up': 'Follow-up Visit',
        'emergency': 'Emergency/Urgent',
        'campaign': 'Campaign Activity',
        'assessment': 'Environmental Assessment'
      };
      return types[type] || type;
    },

    formatAgeAtImmunization(days) {
      if (days < 30) return `${days} days`;
      if (days < 365) return `${Math.floor(days / 30)} months`;
      return `${Math.floor(days / 365)} years`;
    },

    isPastDue(date) {
      if (!date) return false;
      return new Date(date) < new Date();
    },

    getVaccinationStatusColor() {
      if (this.healthProfile.vaccination_status === 'up_to_date') return 'bg-success';
      if (this.healthProfile.vaccination_status === 'partial') return 'bg-warning';
      return 'bg-danger';
    },

    getNutritionalStatusColor() {
      if (this.healthProfile.nutritional_status === 'normal') return 'bg-success';
      if (this.healthProfile.nutritional_status === 'MAM') return 'bg-warning';
      if (this.healthProfile.nutritional_status === 'SAM') return 'bg-danger';
      return 'bg-secondary';
    },

    getNutritionalStatusBadge(status) {
      if (status === 'normal') return 'badge bg-success';
      if (status === 'MAM') return 'badge bg-warning';
      if (status === 'SAM') return 'badge bg-danger';
      return 'badge bg-secondary';
    },

    // Alert methods
    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    }
  }
};
</script>

<style scoped>
.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 600;
}

.nav-tabs-custom .nav-item .nav-link {
  color: #495057;
}

.nav-tabs-custom .nav-item .nav-link.active {
  color: #556ee6;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
</style>